import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  FileText,
  Settings,
  Users,
  ArrowRight,
  TrendingUp,
} from "lucide-react";
import { motion } from "framer-motion";
import AppHeader from "@/components/AppHeader";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatINR, timeAgo } from "@/lib/quotationApi";
import { useAuth, hasRole } from "@/lib/auth";
import { useBranding } from "@/lib/branding";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const role = user?.role || "staff";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/quotations");
        setQuotes(r.data || []);
      } catch (e) {
        console.error("Failed to load quotations:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.active_workspace_id]);

  const totalValue = quotes.reduce((s, q) => s + (q.grand_total || 0), 0);
  const recent = quotes.slice(0, 5);
  const firstName = (user?.name || "there").split(" ")[0];

  const actions = [
    { label: "New quotation", icon: Plus, to: "/new", primary: true, testid: "action-new-quote" },
    { label: "All quotations", icon: FileText, to: "/history", testid: "action-history" },
    ...(hasRole(role, "manager")
      ? [{ label: "Workspace settings", icon: Settings, to: "/admin", testid: "action-settings" }]
      : []),
    ...(hasRole(role, "admin")
      ? [{ label: "Team", icon: Users, to: "/team", testid: "action-team" }]
      : []),
  ];

  return (
    <div className="min-h-screen app-bg text-white" data-testid="dashboard-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="overline text-[#C5A880] mb-2">{branding.firm_name}</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
            Welcome back, {firstName}.
          </h1>
        </motion.div>

        {!loading && <OnboardingChecklist quotesCount={quotes.length} />}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="card-luxe" data-testid="stat-total-quotes">
            <div className="overline text-white/40 mb-1">Quotations</div>
            <div className="font-display font-bold text-3xl">{loading ? "—" : quotes.length}</div>
          </div>
          <div className="card-luxe" data-testid="stat-total-value">
            <div className="overline text-white/40 mb-1 flex items-center gap-1">
              <TrendingUp size={11} /> Pipeline value
            </div>
            <div className="font-display font-bold text-2xl sm:text-3xl truncate">
              {loading ? "—" : formatINR(totalValue)}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {actions.map((a) => (
            <button
              key={a.to}
              onClick={() => navigate(a.to)}
              data-testid={a.testid}
              className={`card-luxe text-left hover:border-[#C5A880]/40 transition group ${
                a.primary ? "!border-[#C5A880]/40" : ""
              }`}
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                  a.primary
                    ? "bg-[#C5A880] text-black"
                    : "bg-[#C5A880]/10 border border-[#C5A880]/30 text-[#C5A880]"
                }`}
              >
                <a.icon size={18} />
              </div>
              <div className="font-medium text-sm flex items-center gap-1">
                {a.label}
                <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition" />
              </div>
            </button>
          ))}
        </div>

        {/* Recent quotations */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-xl">Recent quotations</h2>
          <button
            onClick={() => navigate("/history")}
            className="text-[#C5A880] text-sm hover:underline"
            data-testid="view-all-link"
          >
            View all
          </button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="card-luxe text-center py-12 text-white/40" data-testid="dashboard-empty">
            <FileText size={32} className="mx-auto mb-3 opacity-50" />
            No quotations yet.{" "}
            <button onClick={() => navigate("/new")} className="text-[#C5A880] underline">
              Create your first one
            </button>
            .
          </div>
        ) : (
          <div className="space-y-3">
            {recent.map((q) => (
              <button
                key={q.id}
                onClick={() => navigate(`/quotations/${q.id}`)}
                className="card-luxe w-full text-left hover:border-[#C5A880]/40 transition flex items-center justify-between"
                data-testid={`dashboard-quote-${q.id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{q.customer?.name || "Untitled"}</div>
                  <div className="text-white/40 text-xs truncate">
                    {q.customer?.city || q.customer?.location || "—"}
                    {q.created_by_name ? ` · by ${q.created_by_name}` : ""}
                    {q.last_viewed_at ? ` · viewed ${timeAgo(q.last_viewed_at)}` : ""}
                  </div>
                </div>
                <div className="font-display font-semibold text-[#C5A880] shrink-0 ml-3 flex flex-col items-end gap-1.5">
                  <span>{formatINR(q.grand_total || 0)}</span>
                  <StatusBadge status={q.status} testId={`dashboard-status-${q.id}`} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
